import { TransText } from '@/i18n/trans/text';
import BookingItemLayout from '@/routes/account/layout/BookingItemLayout';
import type { BookingSearchDTO } from '@/types/dto/agent';
import { isTravelPassExpired } from '@/utils/booking';
import { cn } from '@/utils/cn';
import { format } from '@/utils/dateTime';
import { Icons, Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';

interface TravelPassSummaryProps {
  bookingSearchItem: BookingSearchDTO;
  isActive: boolean;
}

const TravelPassSummary: FC<TravelPassSummaryProps> = ({
  bookingSearchItem,
  isActive,
}) => {
  const { travelPassSummary, travelPassDetails, passengers, status } =
    bookingSearchItem;

  const description = travelPassDetails?.productDescription;
  const passengerCount = passengers?.length;
  const isRefunded = Boolean(
    status && ['REFUNDED', 'RELEASED', 'CANCELLED'].includes(status)
  );

  const startTime =
    travelPassDetails?.validFrom &&
    format(travelPassDetails.validFrom, 'EEE, dd MMM yyyy');
  const endTime =
    travelPassDetails?.validUntil &&
    format(travelPassDetails.validUntil, 'EEE, dd MMM yyyy');
  const dateRange =
    startTime && endTime && startTime !== endTime
      ? `${startTime} - ${endTime}`
      : (startTime ?? endTime);

  const isExpired = isTravelPassExpired(bookingSearchItem);

  return (
    <BookingItemLayout
      bookingSearchItem={bookingSearchItem}
      isActive={isActive}
      dateRange={dateRange}
      isExpired={isExpired}
    >
      <div className="flex flex-col gap-1">
        <div
          className={cn('flex flex-wrap items-center gap-x-2 text-primary', {
            'text-neutral': isRefunded,
          })}
        >
          <Typography variant="body1-bold">
            {travelPassSummary?.origin?.name}
          </Typography>
          <Icons.arrows
            className={cn('h-4 w-4', {
              'text-neutral': isRefunded,
            })}
          />
          <Typography variant="body1-bold">
            {travelPassSummary?.destination?.name}
          </Typography>
        </div>
        <div className="flex gap-0.5">
          {description && (
            <Typography variant="body2" className="text-neutral">
              {description}
            </Typography>
          )}
          {description && passengerCount && (
            <Typography variant="body2" className="text-neutral">
              &#8226;
            </Typography>
          )}
          {passengerCount && (
            <>
              <Typography variant="body2" className="text-neutral">
                <TransText
                  i18nKey="passengersCount"
                  values={{ count: passengerCount }}
                />
              </Typography>
            </>
          )}
        </div>
      </div>
    </BookingItemLayout>
  );
};

export default TravelPassSummary;
